import { useEffect, useRef } from 'react';

import type { WorkspaceMember } from '../api';
import { t } from '../i18n';

export const MENTION_MENU_ID = 'mention-menu';
export const mentionOptionId = (id: string): string => `mention-option-${id}`;

const MENTION_WIDTH = 240;
const MENTION_MAX_HEIGHT = 240;
const MENTION_GAP = 4;
const VIEWPORT_MARGIN = 8;

/** Where the picker sits: under the caret, or above it when there is no room. */
export function placeMentionMenu(
  caret: { top: number; bottom: number; left: number },
  viewport: { width: number; height: number },
): { left: number; top?: number; bottom?: number; maxHeight: number } {
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(caret.left, viewport.width - MENTION_WIDTH - VIEWPORT_MARGIN),
  );
  const roomBelow = viewport.height - VIEWPORT_MARGIN - (caret.bottom + MENTION_GAP);
  const roomAbove = caret.top - MENTION_GAP - VIEWPORT_MARGIN;
  if (roomBelow >= MENTION_MAX_HEIGHT || roomBelow >= roomAbove) {
    return {
      left,
      top: caret.bottom + MENTION_GAP,
      maxHeight: Math.min(MENTION_MAX_HEIGHT, roomBelow),
    };
  }
  return {
    left,
    bottom: viewport.height - caret.top + MENTION_GAP,
    maxHeight: Math.min(MENTION_MAX_HEIGHT, roomAbove),
  };
}

interface MentionPickerProps {
  members: WorkspaceMember[];
  activeIndex: number;
  onActiveChange: (index: number) => void;
  onPick: (member: WorkspaceMember) => void;
  caret: { top: number; bottom: number; left: number };
}

/**
 * The people you can name with `@`. Focus stays in the editor: the arrow keys
 * move through the list, this only draws it and takes pointer input.
 */
export function MentionPicker({
  members,
  activeIndex,
  onActiveChange,
  onPick,
  caret,
}: MentionPickerProps): React.ReactElement {
  const active = useRef<HTMLButtonElement | null>(null);
  const placement = placeMentionMenu(caret, {
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    active.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, members]);

  return (
    <div
      id={MENTION_MENU_ID}
      className="mentionmenu"
      role="listbox"
      aria-label={t('mention.label')}
      style={{
        left: placement.left,
        maxHeight: placement.maxHeight,
        ...(placement.top !== undefined ? { top: placement.top } : {}),
        ...(placement.bottom !== undefined ? { bottom: placement.bottom } : {}),
      }}
    >
      {members.length === 0 ? (
        <p className="mentionmenu__empty">{t('mention.nobody')}</p>
      ) : (
        members.map((member, index) => (
          <button
            key={member.id}
            ref={index === activeIndex ? active : undefined}
            id={mentionOptionId(member.id)}
            type="button"
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            className={
              index === activeIndex ? 'mentionmenu__item nb is-active' : 'mentionmenu__item nb'
            }
            onMouseMove={() => {
              // Not mouseenter: a list scrolling under a resting pointer must not take over.
              if (index !== activeIndex) onActiveChange(index);
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              onPick(member);
            }}
          >
            <span className="mentionmenu__avatar" aria-hidden="true">
              {initials(member.name)}
            </span>
            <span className="mentionmenu__name">{member.name}</span>
          </button>
        ))
      )}
    </div>
  );
}

/** Two letters of the name, the way the avatar in the sidebar does it. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? '?';
  if (words.length <= 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? first;
  return `${(first[0] ?? '') + (last[0] ?? '')}`.toUpperCase();
}
